import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'

// Admin-only viewer for the debug_logs table. Email-allowlisted at the
// client (RLS still enforces "your own logs only" — admins read their
// own session traces here, which is enough since we're debugging *our*
// reproductions on remote devices, not other users' data).
//
// To debug another user's session, run a SQL query in the Supabase SQL
// editor with the service role:
//   select * from debug_logs
//   where created_at > now() - interval '1 hour'
//   order by session_id, created_at;

const ADMIN_EMAILS = [
  'mpdjr.2002@gmail.com',
  // Add your dad's email here
]

type DebugLog = {
  id:          string
  session_id:  string
  user_id:     string | null
  feature:     string
  step:        string
  message:     string | null
  payload:     Record<string, unknown> | null
  platform:    string | null
  user_agent:  string | null
  app_version: string | null
  created_at:  string
}

export default function DebugLogs() {
  const [logs,         setLogs]         = useState<DebugLog[]>([])
  const [authChecked,  setAuthChecked]  = useState(false)
  const [authorized,   setAuthorized]   = useState(false)
  const [loading,      setLoading]      = useState(true)

  useEffect(() => { document.title = 'Debug Logs — PantryIO' }, [])

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser()
      const ok = !!user && ADMIN_EMAILS.includes(user.email ?? '')
      setAuthorized(ok)
      setAuthChecked(true)
      if (!ok) { setLoading(false); return }

      const { data, error } = await supabase
        .from('debug_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500)

      if (error) {
        console.error('[DebugLogs] query failed:', error)
      }
      setLogs((data ?? []) as DebugLog[])
      setLoading(false)
    })()
  }, [])

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-forest border-t-transparent animate-spin" />
      </div>
    )
  }

  if (!authorized) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-[24px] text-ink mb-2">Not authorized</h1>
          <p className="font-serif text-[14px] italic text-ink-muted">
            This page is restricted to admins.
          </p>
        </div>
      </div>
    )
  }

  // Group by session_id so each scan attempt is one collapsible block.
  // Sessions are ordered by their newest event, so the most recent
  // attempt sits at the top.
  const sessionMap = new Map<string, DebugLog[]>()
  for (const log of logs) {
    const arr = sessionMap.get(log.session_id) ?? []
    arr.push(log)
    sessionMap.set(log.session_id, arr)
  }
  const sessions = Array.from(sessionMap.entries())
    .map(([sessionId, items]) => {
      // Within a session, oldest first so steps read top-to-bottom.
      const sorted = items.slice().sort((a, b) => a.created_at.localeCompare(b.created_at))
      return { sessionId, items: sorted, latest: items[0] }
    })
    .sort((a, b) => b.latest.created_at.localeCompare(a.latest.created_at))

  return (
    <div className="min-h-screen bg-cream p-6 font-mono text-[12px] text-ink">
      <header className="mb-6 font-sans">
        <h1 className="font-serif text-[28px] text-ink leading-[1.1] tracking-[-0.015em] mb-1">
          Debug <em className="italic">logs</em>
        </h1>
        <p className="font-serif text-[13px] italic text-ink-muted">
          Last 500 events · {sessions.length} session{sessions.length === 1 ? '' : 's'}
          {loading && ' · loading…'}
        </p>
      </header>

      {sessions.length === 0 && !loading && (
        <div className="bg-paper border border-border-sand rounded-2xl p-6 text-center font-sans">
          <p className="font-serif text-[14px] italic text-ink-muted">
            No logs yet. Reproduce the bug on a device to populate this page.
          </p>
        </div>
      )}

      {sessions.map(({ sessionId, items, latest }) => {
        const stepCount = items.length
        const platform  = latest.platform ?? 'unknown'
        const version   = latest.app_version ?? 'unknown'
        const feature   = latest.feature
        const lastStep  = items[items.length - 1]?.step ?? '?'
        return (
          <details
            key={sessionId}
            className="mb-3 bg-paper border border-border-sand rounded-2xl overflow-hidden"
          >
            <summary className="cursor-pointer px-4 py-3 hover:bg-cream-warm font-sans text-[13px] flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[11px] text-ink-muted">{sessionId.slice(0, 8)}</span>
              <span className="font-medium text-ink">{feature}</span>
              <span className="text-ink-muted">· {stepCount} events</span>
              <span className="text-ink-muted">· last: <span className="text-terracotta">{lastStep}</span></span>
              <span className="ml-auto text-[11px] text-ink-muted">
                {platform} · v{version} · {new Date(latest.created_at).toLocaleString()}
              </span>
            </summary>
            <div className="border-t border-border-hairline">
              {items.map((log, idx) => (
                <div
                  key={log.id}
                  className={`px-4 py-2 ${idx % 2 ? 'bg-cream-warm/40' : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-ink-muted shrink-0 w-[170px] truncate">
                      {new Date(log.created_at).toISOString().slice(11, 23)} · +{
                        idx === 0
                          ? '0ms'
                          : `${new Date(log.created_at).getTime() - new Date(items[idx - 1].created_at).getTime()}ms`
                      }
                    </span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-forest">{log.step}</span>
                      {log.message && <span className="text-ink ml-2">{log.message}</span>}
                      {log.payload && (
                        <pre className="mt-1 text-[11px] text-ink-muted overflow-x-auto whitespace-pre-wrap break-all">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </details>
        )
      })}
    </div>
  )
}
